import React from "react";
import { FlatList } from "react-native";
import { getLanguage } from "@microdoing/shared/services/profileSettings/selectors";
import { getCampaignSelected } from "@microdoing/shared/services/userCampaign/selectors";
import { getClientLogo } from "@microdoing/shared/services/client/selectors";
import { Box, MainButton, MainText, MainTitle } from "app/components/common";
import theme from "app/Theme/Theme";
import normalize from "app/utils/normalize";
import selectLanguage from "app/utils/selectLanguage";
import FastImage from "react-native-fast-image";
import { useSelector } from "react-redux";
import i18n from "@microdoing/shared/i18n";

const LongDarkLogo = require("./../../../assets/images/long-dark-logo.png");
const RocketImage = require("./../../../assets/images/campaign-rocket.png");
const ArrowImage = require("./../../../assets/images/campaign-arrow.png");

const DEFAULT_PICTURE =
  "https://pulse-experience.s3.eu-west-3.amazonaws.com/images/background.png";

interface IProps {
  goNext: () => void;
}

const WebNewCampaignModal = ({ goNext }: IProps) => {
  const campaignSelected = useSelector(getCampaignSelected);
  const clientLogo = useSelector(getClientLogo);
  const language = useSelector(getLanguage);

  const { picture, title, skills } = campaignSelected;

  const renderSkills = ({ item, index }) => {
    return (
      <Box
        backgroundColor="brand.100"
        justifyContent="center"
        alignItems="center"
        borderRadius={16}
        marginRight={index % 2 === 0 ? "xs" : 0}
        marginBottom="xs"
        paddingHorizontal="xs"
        paddingVertical="xxs"
        flex={1 / 2}
      >
        <MainText variant="l1-medium" textAlign="center">
          {selectLanguage({
            text: item.label,
            language,
          })}
        </MainText>
      </Box>
    );
  };

  return (
    <Box flex={1} justifyContent="center" backgroundColor="neutral.white">
      <Box variant="webOnboarding" flexDirection="column">
        <Box height="50%" backgroundColor="neutral.50">
          <Box
            flex={1}
            borderTopLeftRadius={theme.breakpoints.webMedium ? 20 : 0}
            borderTopRightRadius={theme.breakpoints.webMedium ? 20 : 0}
            backgroundColor="neutral.50"
            paddingLeft="webXXL"
            paddingTop="webXXL"
          >
            <Box width={normalize(344)} style={{ marginBottom: "130px" }}>
              <FastImage
                resizeMode="contain"
                source={LongDarkLogo}
                style={styles.webLongLogo}
              />
              <MainText variant="l2-medium" color="neutral.400" marginTop="sm">
                {i18n.t("web-intro-text-logo")}
              </MainText>
            </Box>
          </Box>
        </Box>
        <Box position="absolute" top={179} right={-27}>
          <FastImage
            resizeMode="contain"
            source={RocketImage}
            style={styles.rocketImage}
          />
        </Box>
        <Box position="absolute" bottom={19}>
          <FastImage
            resizeMode="contain"
            source={ArrowImage}
            style={styles.arrowImage}
          />
        </Box>
        <Box position="absolute" alignSelf="center" top={208}>
          <MainTitle variant="l7-bold" marginBottom="l">
            {i18n.t("your-first-campaign")}
          </MainTitle>
          <Box
            borderColor="transparent"
            shadowColor="neutral.900"
            shadowOpacity={0.25}
            shadowRadius={27}
            backgroundColor="neutral.white"
            borderRadius={8}
            paddingBottom="m"
            marginBottom="l"
            style={{
              shadowOffset: {
                height: 4,
                width: 0,
              },
            }}
            width={312}
          >
            <Box borderRadius={8} marginBottom="xm">
              <FastImage
                resizeMode="cover"
                source={picture || DEFAULT_PICTURE}
                style={styles.campaignImage}
              />
            </Box>
            <Box marginLeft="xm" marginRight="sm">
              <MainText variant="l5-bold">
                {selectLanguage({
                  text: title,
                  language,
                })}
              </MainText>
              <Box marginTop="sm" flexDirection="row">
                <Box flex={2}>
                  <FlatList
                    numColumns={2}
                    data={skills}
                    renderItem={renderSkills}
                    style={{
                      width: "100%",
                      display: "flex",
                      paddingHorizontal: "8",
                    }}
                  />
                </Box>
                <Box alignItems="center" justifyContent="center" marginLeft="s">
                  <FastImage
                    resizeMode="contain"
                    source={clientLogo}
                    style={{ width: 50, height: 50 }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
          <Box width={312} alignItems="center">
            <Box width={304}>
              <MainButton onPress={goNext} text={i18n.t("start")} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const styles = {
  webLongLogo: {
    height: normalize(16),
    width: normalize(110),
  },
  rocketImage: {
    height: 269,
    width: 357,
  },
  arrowImage: {
    height: 239,
    width: 317,
  },
  campaignImage: {
    height: 102,
    width: 312,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
};

export default WebNewCampaignModal;
